import { all, takeEvery, fork, put, select, call, cancelled, takeLatest } from 'redux-saga/effects';
import { studentsActionTypes } from '.';
import StudentsService from '../../../services/students';
import { executeErrorCode } from '../../auth/actions';
import { studentsActionsInterfaces, studentsActions } from '.';
import { staticViewsActions, staticViewsActionTypes, staticViewsSelectors } from '..';
import DataDestructuring from '../../../helpers/DataDestructuring';
import StringBuilder from '../../../helpers/StringBuilder';
import UrlBuilder from '../../../helpers/urlBuilder';

const marksColumns: string[] = ['ocenaKoncowa', 'formyZajec'];
const sharedColumns = ['ocenaWyliczona'];
const mandatoryColumns: string[] = [
  'studentId',
  'studentImie',
  'studentNazwisko',
  'rokAkademicki',
  'semestrAkademicki',
  'przedmiotId',
  'kierunekNazwa',
  'rodzajStudiow',
];

const applicableView = 'Students';

export function* initStudentsWatcher(): Generator {
  yield takeEvery(studentsActionTypes.INIT, initStudents);
}

export function* initStudents(action: studentsActionsInterfaces.Init): Generator {
  const { view } = action.payload;
  const { loading, data, userColumnsSetup } = (yield select(state =>
    staticViewsSelectors.getStaticViewState(state, view),
  )) as ViewDataSet;
  if (!loading && !data.length) {
    try {
      /*
        czekamy, aż wykonają się sagi pobierające config filtrów i kolumn
        - kolumny pobieramy tylko, gdy userColumnsSetup jest pusty - unikamy niepotrzebnego requestu (mógł być pobrany wcześniej, jeśli została zainicjalizowana już dowolna zakładka studenta, która korzysta z tego samego configu)
        - call - zgodnie z dokumentacją jako argumentu nie można podać obiektu dispatchowanej akcji.
          W tym przypadku podawany jest generator a kolejne argumenty to argumenty przekazywane do tego generatora
          (w tym przypadku saga przyjmuje obiekt akcji). Oznacza to, że nie jest dispatchowana akcja w redux.
      */
      yield all([
        call(getStudentsFiltersList, studentsActions.getFiltersList(view)),
        !userColumnsSetup.details.length && !userColumnsSetup.main.length ? put(studentsActions.getColumnsConfig(view)) : null,
      ]);
      yield put(studentsActions.getData(view));
      yield put(staticViewsActions.initFinish(view));
    } catch (error) {
      if (error && error.errorCode) {
        yield put(executeErrorCode(error.errorCode, action, error));
      }
    }
  }
}
export function* getStudentsWatcher(): Generator {
  yield takeLatest(studentsActionTypes.GET_DATA_START, getStudents);
}
/**
 * pobieranie danych do tabeli "Studenci"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudents(action: studentsActionsInterfaces.GetData): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { search, sorting, userColumnsSetup, page, filters } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const columns = [...userColumnsSetup.main];
    mandatoryColumns.forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
    const requestAtributes = {
      filter: filters.allFilters.map((filters: any) => ({ name: filters.name, values: filters.selected })),
      page: page.toString(),
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      search,
    };
    const response: any = yield call(StudentsService.getStudents, requestAtributes, abortController.signal);
    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.studentPage,
      data: response.studentPage.data.map((obj: any) => ({
        ...obj,
        key: obj.studentId,
      })),
    };
    yield put(staticViewsActions.getDataSuccess(view, payload));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getDataFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* getStudentsNextPageWatcher(): Generator {
  yield takeEvery(studentsActionTypes.GET_DATA_NEXT_PAGE_START, getStudentsNextPage);
}
/**
 * dobieranie kolejnej partii danych do tabeli "Studenci"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getStudentsNextPage(action: studentsActionsInterfaces.GetDataNextPage): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { userColumnsSetup, sorting, page, search, filters } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const columns = [...userColumnsSetup.main];
    mandatoryColumns.forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
    const requestAtributes = {
      filter: filters.allFilters.map((filters: any) => ({ name: filters.name, values: filters.selected })),
      page: page + 1,
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      search,
    };
    const response: any = yield call(StudentsService.getStudents, requestAtributes, abortController.signal);
    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.studentPage,
      data: response.studentPage.data.map((obj: any) => ({
        ...obj,
        key: obj.studentId,
      })),
    };
    yield put(staticViewsActions.getDataNextPageSuccess(view, payload));
    yield put(staticViewsActions.setPage(action.payload.view, page + 1));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getDataNextPageFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

/*
  Poniższy generator nie ma watchera, ponieważ nie jest wywoływany z zewnątrz (np. z komponentu),
  a poprzez call w generatorze initStudents. Call nie dispatchuje akcji w redux.
*/
export function* getStudentsFiltersList(action: studentsActionsInterfaces.GetFiltersList): Generator {
  const { view } = action.payload;
  try {
    const response: any = yield call(StudentsService.getStudentsFilterList);
    if (response) {
      yield put(staticViewsActions.getFiltersConfigSuccess(view, response.fieldFilter));
      const isDefault: object | undefined = response.fieldFilter.find((item: any) => item.isDefault);
      if (isDefault) {
        yield put(studentsActions.refreshFilterValues(view));
      }
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getFiltersConfigFail(view));
  }
}

export function* refreshFilterValuesWatcher(): Generator {
  yield takeLatest(studentsActionTypes.FILTERS_REFRESH_VALUES_START, refreshFilterValues);
}
/**
 * aktualizacja wartości filtrów
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* refreshFilterValues(action: studentsActionsInterfaces.RefreshFilterValues): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { filters } = (yield select(state => staticViewsSelectors.getStaticViewState(state, view))) as ViewDataSet;
    /* budowa parametrów do prośby o aktualizacje listy filtrów */
    const requestAttributes: FilterValuePayload = {
      filter: filters.allFilters
        .filter(filter => filter.active)
        .reduce((acc: object, filter) => ({ ...acc, [filter.name]: DataDestructuring.returnMultiFilterData(filter) }), {}),
    };
    const response: any = yield call(StudentsService.refreshFilterValues, requestAttributes, abortController.signal);
    yield put(staticViewsActions.refreshFilterValueSuccess(view, response.response));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.refreshFilterValueFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* saveColumnsUserSetupWatcher(): Generator {
  yield takeEvery(studentsActionTypes.COLUMNS_SAVE_USER_SETUP_START, saveColumnsUserSetup);
}
/**
 * zapisanie konfiguracji kolumn użytkownika dla tabeli "Studenci"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* saveColumnsUserSetup(action: studentsActionsInterfaces.SaveColumnsUserSetup): Generator {
  const { view, selectedColumns } = action.payload;
  try {
    yield call(StudentsService.saveColumnsUserSetup, { columnsSetup: selectedColumns });
    /*
      Akcja saveColumnsUserSetupSuccess służy poinformowaniu zakładek o typie strona-przedmiotu o zmianie konfiguracji kolumn,
      dzięki czemu będą mogły one uruchomić akcję odświeżenia danych.
    */
    yield put(studentsActions.saveColumnsUserSetupSuccess(view));
    yield put(studentsActions.getColumnsConfig(view));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getColumnsConfigWatcher(): Generator {
  yield takeEvery(studentsActionTypes.COLUMNS_GET_CONFIG_START, getColumnsConfig);
}
/**
 * pobieranie konfiguracji kolumn dla tabeli "Studenci" i "Strona przedmiotu"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */

export function* getColumnsConfig(action: studentsActionsInterfaces.GetColumnsConfig): Generator {
  const { view } = action.payload;

  try {
    const response: any = yield StudentsService.getColumnsConfig();
    const marksCols = response.przedmiotField
      .map((col: SingleColumn) => (sharedColumns.includes(col.dataIndex) || marksColumns.includes(col.dataIndex) ? col : null))
      .filter((col: SingleColumn) => col !== null);
    const commonColumns = response.przedmiotField
      .map((col: SingleColumn) => (sharedColumns.includes(col.dataIndex) || !marksColumns.includes(col.dataIndex) ? col : null))
      .filter((col: SingleColumn) => col !== null);

    yield put(staticViewsActions.getColumnsConfigSuccess(view, commonColumns, marksCols));
    yield put(staticViewsActions.saveColumnsUserSetupSuccess(view, response.przedmiotField));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getColumnsConfigFail(view));
  }
}

export function* refreshStudentsDataWatcher(): Generator {
  yield takeEvery(
    [
      staticViewsActionTypes.SET_ORDER,
      staticViewsActionTypes.COLUMNS_GET_CONFIG_SUCCESS,
      studentsActionTypes.REFRESH_DATA_START,
      studentsActionTypes.FILTERS_REFRESH_VALUES_START,
    ],
    refreshStudentsData,
  );
}
/**
 * odświeżenie listy
 *
 * @export
 * @return {*}  {Generator}
 *
 */
export function* refreshStudentsData(action: studentsActionsInterfaces.RefreshData): Generator {
  const { view } = action.payload;
  const { initTable, selected } = (yield select(state => staticViewsSelectors.getStaticViewState(state, view))) as ViewDataSet;
  if (view === applicableView && !initTable) {
    // yield put(staticViewsActions.setLoading(action.payload.view));
    // yield call(getStudents, studentsActions.getData(action.payload.view));
    yield put(studentsActions.getData(action.payload.view));
    if (selected.length === 1) {
      yield put(studentsActions.getRecordDetails(view, selected[0]));
    }
  }
}

export function* getStudentsGetDetailsWatcher(): Generator {
  yield takeEvery(studentsActionTypes.RECORD_GET_DETAILS_START, getStudentsGetDetails);
}
/**
 * pobieranie szczegółów wybranego rekordu
 *
 * @export
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* getStudentsGetDetails(action: studentsActionsInterfaces.GetRecordDetails): Generator {
  const { view, id } = action.payload;
  try {
    const { data, userColumnsSetup } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const selectedRow = data.find((row: any) => row.studentId === id);
    if ((selectedRow && !selectedRow.details) || (selectedRow && Object.getOwnPropertyNames(selectedRow.details).length === 0)) {
      const semestrAkademicki = StringBuilder.buildSemester(selectedRow.rokAkademicki, selectedRow.semestrAkademicki);
      const przedmiotId = selectedRow.przedmiotId;
      const columns = [...userColumnsSetup.details];
      mandatoryColumns.forEach(key => {
        if (!columns.includes(key)) {
          columns.push(key);
        }
      });
      const requestParams = {
        filter: UrlBuilder.generateFiltersArray({ przedmiotId, semestrAkademicki }),
        columns,
        id,
      };
      const response: any = yield call(StudentsService.getStudentDetails, requestParams);
      yield put(staticViewsActions.getRecordDetailsSuccess(view, { ...response.student, semestrAkademicki }, id));
    } else {
      yield put(staticViewsActions.setDetailsLoading(view));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getRecordDetailsFail(view));
  }
}

export default function* rootSaga(): Generator {
  yield all([
    fork(initStudentsWatcher),
    fork(getStudentsWatcher),
    fork(getStudentsNextPageWatcher),
    fork(refreshStudentsDataWatcher),
    fork(refreshFilterValuesWatcher),
    fork(saveColumnsUserSetupWatcher),
    fork(getStudentsGetDetailsWatcher),
    fork(getColumnsConfigWatcher),
  ]);
}
